import type { PluginWebManifest } from '@reflex/plugin-api/web';

const PKG = '@reflex/plugin-marketing';

/**
 * Web manifest for the marketing plugin. Lives in its own module so both
 * the plugin definition (`src/index.ts`) and the web registration
 * (`src/web/register.ts`) can import it. The SPA reads it via
 * `createWebRegistrationFromManifest` to resolve public marketing routes
 * statically — anonymous visitors hit `/pages/:slug` before the
 * auth-gated `GET /api/config/plugins` fetch resolves, so static
 * resolution is required for the live link to work without a session.
 */
export const marketingWeb: PluginWebManifest = {
  // Logged-out home page rendered at `/` for anonymous visitors. Default
  // priority (0) so a deployment-specific marketing plugin (e.g. a hosted
  // Runloop marketing site) can override it with any positive priority.
  homePage: {
    componentPath: `${PKG}/web/MarketingHomePage`,
  },
  routes: [
    {
      path: '/pages',
      componentPath: `${PKG}/web/DynamicMarketingIndexPage`,
      public: true,
      marketing: {
        label: 'Overview',
        description: 'Browse every published marketing page.',
        icon: 'Compass',
        order: 0,
      },
    },
    {
      path: '/pages/:slug',
      componentPath: `${PKG}/web/DynamicMarketingPage`,
      public: true,
      marketing: {
        label: 'Page',
        description: 'A dynamically-rendered marketing page.',
        icon: 'Box',
        order: 10,
      },
    },
    {
      path: '/plugins/marketing',
      componentPath: `${PKG}/web/admin/MarketingAdminHubPage`,
    },
    {
      path: '/plugins/marketing/:id/edit',
      componentPath: `${PKG}/web/admin/MarketingPageEditorPage`,
    },
    {
      // Authed, full-viewport preview of a page's current **draft**
      // blocks — the destination for the editor's "Preview draft"
      // button. Distinct from the public `/pages/:slug` route, which
      // only renders the published snapshot.
      path: '/plugins/marketing/:id/preview',
      componentPath: `${PKG}/web/admin/MarketingPagePreviewPage`,
      chromeless: true,
    },
    {
      // Legacy `/waitlist` entrypoint. The alpha waitlist has been retired;
      // this route now redirects to the standard `/signup` registration page
      // so any lingering bookmarks or links keep working.
      path: '/waitlist',
      componentPath: `${PKG}/web/AlphaWaitlistPage`,
      public: true,
      chromeless: true,
    },
  ],
  navItems: [
    {
      to: '/plugins/marketing',
      label: 'Marketing',
      icon: 'Megaphone',
      group: 'plugins',
    },
  ],
  overviewCards: [
    {
      to: '/plugins/marketing',
      label: 'Marketing',
      icon: 'Megaphone',
      description: 'Editable public marketing pages with visual editor and chat.',
      details:
        'Create, edit, and publish marketing pages served at shareable public URLs. Edit blocks in a visual editor or ask the built-in chat agent to make changes using typed tools.',
      features: ['Visual editor', 'Chat-driven edits', 'Publish / unpublish'],
      group: 'plugins',
    },
  ],
};
